import { findStaleFilterValues } from 'features/charts-v2/find-stale-filter-values'
import type { SelectedFilterOption } from 'features/charts-v2/types'
// bully ignore[no-cross-feature-private-imports: collapseThemeDisplayName is FilterCard.tsx's own theme-collapsing primitive (getDisplayValue); reusing it here keeps this file's UUID->display-name resolution identical to the sidebar's, rather than re-deriving the "Parent: Parent" collapse rule a second time. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { collapseThemeDisplayName } from 'features/charts-v2/utils/filter-dedup'
import type { AppliedFilterOption, FilterOption } from 'features/filters/types'
import {
  ALIAS_FOR_NULL_VALUES,
  isNullValueAlias,
} from 'src/constants/filter-values'
import type { FilterPreviewEntry } from 'src/features/dashboards-v2-1/features/embeddables/components/FilterPreview/FilterPreview'

/**
 * Adapts a v1 `AppliedFilterOption` (a widget tile's own filter shape) onto
 * the v2 `SelectedFilterOption` shape `buildFilterPreviewEntry` reads —
 * v1 carries no operator, so it defaults to `'is'` (matching how
 * `FilterCard.tsx` treats an absent operator).
 */
export function appliedFilterOptionToSelectedFilterOption(
  applied: AppliedFilterOption,
): SelectedFilterOption {
  const range = applied as { min?: number; max?: number; value_type?: number }
  const select = applied as { values?: string[] }
  return {
    field: applied.field as SelectedFilterOption['field'],
    operator: 'is',
    values: select.values ?? [],
    min: range.min,
    max: range.max,
    valueType: range.value_type,
  }
}

/** Resolves a theme/proj_uuid value's raw stored value (a cluster UUID) to
 *  its display name via the catalogue, matching
 *  `FilterCard.getDisplayValue`'s lookup. Falls through unchanged for every
 *  other field. */
function resolveDisplayValue(
  field: string,
  value: string,
  catalogueEntry: FilterOption | undefined,
): string {
  if (
    (field === 'theme' || field === 'proj_uuid') &&
    catalogueEntry &&
    'values' in catalogueEntry
  ) {
    const option = catalogueEntry.values?.find(
      (v) => v.value === value || v.cluster_uuid === value,
    )
    const name = option?.value ?? value
    return field === 'theme' ? collapseThemeDisplayName(name) : name
  }
  if (isNullValueAlias(value)) return ALIAS_FOR_NULL_VALUES
  return value
}

/** The "A, B, or N more" truncation `FilterCard.getFieldValue` uses. */
function truncateDisplayValues(values: string[]): string {
  if (values.length === 1) return values[0]!
  if (values.length === 2) return values.join(', ')
  return `${values.slice(0, 2).join(', ')}, or ${values.length - 2} more`
}

/** Every value, comma-joined — no truncation. Used where the caller's whole
 *  point is showing WHICH values are involved (the tile filter-conflict
 *  message), so hiding them behind "or N more" would defeat the purpose. */
function joinAllDisplayValues(values: string[]): string {
  return values.join(', ')
}

/** The deduped, display-resolved value list for a selection — empty for a
 *  range/numeric/no-value selection. This is the same leaf-value list the
 *  sidebar's filter-count badge sums per field
 *  (`reducerToComputeFiltersCount` in `features/filters/utils/helpers.ts`),
 *  so callers counting filter values should read its length rather than
 *  re-deriving a second count. */
function resolveDisplayValues(
  selection: SelectedFilterOption,
  catalogueEntry: FilterOption | undefined,
): string[] {
  if (selection.values.length === 0) return []
  return [
    ...new Set(
      selection.values.map((v) =>
        resolveDisplayValue(selection.field, v, catalogueEntry),
      ),
    ),
  ]
}

function formatFilterValue(
  selection: SelectedFilterOption,
  displayValues: string[],
  truncate: boolean,
): string {
  if (displayValues.length === 0) {
    if (selection.min !== undefined && selection.max !== undefined) {
      return `${selection.min} - ${selection.max}`
    }
    if (selection.numericValue !== undefined) {
      return String(selection.numericValue)
    }
    return 'N/A'
  }
  return truncate
    ? truncateDisplayValues(displayValues)
    : joinAllDisplayValues(displayValues)
}

/**
 * One `SelectedFilterOption` → one read-only `FilterPreviewEntry`. No model
 * prefix by default — the tile preview reads "Brand", not "Product > Brand"
 * (the sidebar's convention); `FilterPreviewEntry.model` stays supported for
 * a caller that wants it, this builder just never sets it.
 *
 * `truncate` (default `true`, the tile-pill preview's need) collapses a long
 * value list to "A, B, or N more". Pass `false` when the whole point is
 * showing WHICH values are involved — e.g. the tile filter-conflict message
 * (`TileFilterConflictState`), where hiding values behind "or N more" would
 * defeat the purpose.
 */
export function buildFilterPreviewEntry(
  selection: SelectedFilterOption,
  accent: 'local' | 'global',
  superseded: boolean,
  filterOptions: FilterOption[],
  truncate = true,
): FilterPreviewEntry {
  const catalogueEntry = filterOptions.find((o) => o.field === selection.field)
  const displayValues = resolveDisplayValues(selection, catalogueEntry)
  const staleValues =
    findStaleFilterValues([selection], filterOptions)[0]?.values ?? []

  return {
    ...(staleValues.length ? { staleValues } : {}),
    id: `${accent}-${selection.field}`,
    field: selection.field,
    label: catalogueEntry?.alias ?? selection.field,
    operator: selection.operator,
    value: formatFilterValue(selection, displayValues, truncate),
    // A range/numeric/no-value selection has no leaf values to count but is
    // still one applied filter, matching how the sidebar's badge counts a
    // range group as one via `getBadgeLabels`' single-string return.
    valueCount: displayValues.length || 1,
    accent,
    superseded,
  }
}
